// YouTube URL에서 비디오 ID 추출
export const extractYouTubeVideoId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// YouTube Audio ID 추출 (YouTube Music도 지원)
export const extractYouTubeAudioId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|music\.youtube\.com\/watch\?v=)([^#&?]*)/,
    /^([a-zA-Z0-9_-]{11})$/, // 직접 ID 입력
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1] && match[1].length === 11) {
      return match[1];
    }
  }
  return null;
};

// URL 유효성 검사
export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// 이미지 파일 검증
export const validateImageFile = (file) => {
  if (!file) return { valid: false, message: "파일을 선택해주세요." };

  // 파일 크기 체크 (5MB 제한)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      message: `파일 크기가 너무 큽니다. 5MB 이하의 파일을 선택해주세요. (현재: ${(
        file.size /
        (1024 * 1024)
      ).toFixed(1)}MB)`,
    };
  }

  // 파일 타입 체크
  const allowedTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      message: "지원하지 않는 이미지 형식입니다. (JPG, PNG, GIF, WebP만 지원)",
    };
  }

  return { valid: true };
};

// 비디오 파일 검증
export const validateVideoFile = (file) => {
  if (!file) return { valid: false, message: "파일을 선택해주세요." };

  // 파일 크기 체크 (100MB 제한)
  const maxSize = 100 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      message: `파일 크기가 너무 큽니다. 100MB 이하의 파일을 선택해주세요. (현재: ${(
        file.size /
        (1024 * 1024)
      ).toFixed(1)}MB)`,
    };
  }

  // 파일 타입 체크
  const allowedTypes = [
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/avi",
    "video/mov",
  ];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      message:
        "지원하지 않는 비디오 형식입니다. (MP4, WebM, OGG, AVI, MOV만 지원)",
    };
  }

  return { valid: true };
};

// 음악 파일 검증
export const validateMusicFile = (file) => {
  if (!file) return { valid: false, message: "파일을 선택해주세요." };

  // 파일 크기 체크 (10MB 제한)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      valid: false,
      message: `파일 크기가 너무 큽니다. 10MB 이하의 파일을 선택해주세요. (현재: ${(
        file.size /
        (1024 * 1024)
      ).toFixed(1)}MB)`,
    };
  }

  // 파일 타입 체크
  const allowedTypes = [
    "audio/mp3",
    "audio/mpeg",
    "audio/wav",
    "audio/ogg",
    "audio/aac",
  ];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      message: "지원하지 않는 음악 형식입니다. (MP3, WAV, OGG, AAC만 지원)",
    };
  }

  return { valid: true };
};

// PIN 검증
export const validatePin = (pin) => {
  if (!pin || typeof pin !== "string") {
    return {
      valid: false,
      message: "PIN을 입력하세요.",
    };
  }

  if (pin.length !== 6) {
    return {
      valid: false,
      message: "PIN은 6자리여야 합니다.",
    };
  }

  if (!/^\d{6}$/.test(pin)) {
    return {
      valid: false,
      message: "PIN은 숫자 6자리여야 합니다.",
    };
  }

  return {
    valid: true,
    message: "PIN이 유효합니다.",
  };
};

// PIN 형식 정리
export const sanitizePin = (pin) => {
  if (!pin) return "";

  // 숫자가 아닌 문자 제거
  let cleaned = pin.toString().replace(/[^0-9]/g, "");

  // 6자리로 제한
  if (cleaned.length > 6) {
    cleaned = cleaned.substring(0, 6);
  }

  return cleaned;
};

// 음악 재생 시간 검증
export const validateMusicDuration = (duration) => {
  if (duration === null || duration === undefined || duration === "") {
    return { valid: true, message: "전체 재생으로 설정됩니다." };
  }

  const numDuration = parseInt(duration);

  if (isNaN(numDuration)) {
    return { valid: false, message: "재생 시간은 숫자여야 합니다." };
  }

  if (numDuration < 10) {
    return {
      valid: false,
      message: "재생 시간은 최소 10초 이상이어야 합니다.",
    };
  }

  if (numDuration > 3600) {
    return {
      valid: false,
      message: "재생 시간은 최대 3600초(1시간) 이하여야 합니다.",
    };
  }

  return { valid: true, message: `${numDuration}초 재생으로 설정됩니다.` };
};

// 시간 포맷팅 함수 (초 -> "분:초" 형식)
export const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) {
    return "전체 재생";
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}분 ${remainingSeconds}초`;
  } else {
    return `${remainingSeconds}초`;
  }
};

// base64 데이터 크기 추정
export const estimateBase64Size = (base64Data) => {
  const base64Size = base64Data.length * 0.75;
  return base64Size / (1024 * 1024); // MB 단위로 반환
};

// 파일을 base64로 변환
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// 컨텐츠 지속시간 검증
export const validateContentDuration = (duration) => {
  const numDuration = parseInt(duration);

  if (isNaN(numDuration) || numDuration < 1) {
    return { valid: false, message: "지속시간은 1초 이상이어야 합니다." };
  }

  if (numDuration > 3600) {
    return {
      valid: false,
      message: "지속시간은 최대 3600초(1시간) 이하여야 합니다.",
    };
  }

  return { valid: true };
};

// 브라우저 호환성 체크
export const checkBrowserCompatibility = () => {
  const warnings = [];

  // 필수 기능 체크
  if (!window.localStorage) {
    warnings.push("브라우저가 로컬 스토리지를 지원하지 않습니다.");
  }

  if (!window.JSON) {
    warnings.push("브라우저가 JSON을 지원하지 않습니다.");
  }

  if (!document.querySelector) {
    warnings.push("브라우저가 querySelector를 지원하지 않습니다.");
  }

  // 오디오/비디오 지원 체크
  const audio = document.createElement("audio");
  if (!audio.canPlayType) {
    warnings.push("브라우저가 오디오 재생을 지원하지 않을 수 있습니다.");
  }

  const video = document.createElement("video");
  if (!video.canPlayType) {
    warnings.push("브라우저가 비디오 재생을 지원하지 않을 수 있습니다.");
  }

  return {
    compatible: warnings.length === 0,
    warnings: warnings,
  };
};

// 디바운스 함수
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 에러 메시지 파싱
export const parseErrorMessage = (error) => {
  if (typeof error === "string") {
    return error;
  }

  if (error?.message) {
    return error.message;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  return "알 수 없는 오류가 발생했습니다.";
};

// 현황판 URL 생성 함수
export const generateDashboardUrl = (tvId, options = {}) => {
  // 새로운 /{tvName} 형태로 변경
  const baseUrl = window.location.origin;
  const url = new URL(`${baseUrl}/${encodeURIComponent(tvId)}`);

  // 추가 옵션들
  if (options.kiosk) {
    url.searchParams.set("kiosk", "true");
  }

  if (options.preview) {
    url.searchParams.set("preview", "1");
  }

  if (options.autostart) {
    url.searchParams.set("autostart", "true");
  }

  return url.toString();
};
